import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useApiClient } from '~/api-client';
import { HospitalForm, HospitalsResource } from '@mark43/cad-api';
import {
    ButtonGroup,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    Flex,
    VStack,
    FormRow,
    Drawer,
} from '@cad-arc';
import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../forms/integrated-form-controls/form-location-select';
import { FormTextarea } from '../forms/integrated-form-controls/form-text-area';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormAttributeSelect } from '../forms/integrated-form-controls/form-attribute-select';

export function EditHospitalDrawer({ hospitalId }: { hospitalId: number }) {
    const drawerControls = useDrawerControls();
    const apiClient = useApiClient();

    const hospitalForm = useForm<HospitalForm>();

    const closeDrawer = useCallback(() => {
        drawerControls.close();
    }, [drawerControls]);

    const hospitalQuery = useQuery(
        ['hospital-drawer'],
        () => {
            return apiClient.request<HospitalsResource.EditHospitalInAppBootstrap>({
                method: 'POST',
                url: `hospitals/in_app/bootstrap`,
                data: {
                    hospitalId,
                },
            });
        },
        {
            onSuccess: (response) => {
                const hospitalData = response?.data?.data?.hospitalForm;
                if (hospitalData) {
                    hospitalForm.reset(hospitalData);
                }
            },
        }
    );

    const hospitalMutation = useMutation(
        ['save-hospital-form', {}],
        (form: HospitalForm) => {
            return apiClient.request<HospitalsResource.EditHospitalInApp>({
                method: 'POST',
                url: `hospitals/in_app/`,
                data: {
                    hospitalForm: form,
                },
            });
        },
        {
            onSuccess: () => {
                closeDrawer();
            },
        }
    );

    return (
        <IntegratedFormProvider
            query={hospitalQuery}
            mutation={hospitalMutation}
            form={hospitalForm}
            onSubmit={(form) => hospitalMutation.mutate(form)}
        >
            <Drawer
                isOpen
                size="lg"
                variant="overlay"
                onClose={closeDrawer}
                closeOnOverlayClick={false}
            >
                <Flex height="100%" direction="column">
                    <DrawerHeader>Edit Hospital</DrawerHeader>
                    <DrawerBody>
                        <FormBody>
                            <FormProvider {...hospitalForm}>
                                <VStack gap={1}>
                                    <FormRow>
                                        <FormInput
                                            label="Name"
                                            name="hospital.name"
                                            isDisabled={true}
                                            control={hospitalForm.control}
                                        />
                                    </FormRow>
                                    <FormRow>
                                        <FormLocationSelect
                                            name="location"
                                            label="Location"
                                            control={hospitalForm.control}
                                            isDisabled={true}
                                        />
                                    </FormRow>
                                    <FormRow>
                                        <FormAttributeSelect
                                            attributeType="HOSPITAL_CAPABILITY"
                                            isMultiple
                                            name="hospitalCapabilityAttributeOptions"
                                            label="Capabilities"
                                            control={hospitalForm.control}
                                        />
                                    </FormRow>
                                    <FormRow>
                                        <FormAttributeSelect
                                            attributeType="HOSPITAL_STATUS"
                                            name="hospitalStatusAttributeOption"
                                            label="Status"
                                            control={hospitalForm.control}
                                            rules={{ required: 'Required' }}
                                        />
                                    </FormRow>
                                    <FormRow>
                                        <FormTextarea
                                            label="Notes"
                                            name="hospital.notes"
                                            control={hospitalForm.control}
                                        />
                                    </FormRow>
                                </VStack>
                            </FormProvider>
                        </FormBody>
                    </DrawerBody>

                    <DrawerFooter>
                        <ButtonGroup>
                            <FormCancelButton onClick={closeDrawer} />
                            <FormSaveButton>Save</FormSaveButton>
                        </ButtonGroup>
                    </DrawerFooter>
                </Flex>
            </Drawer>
        </IntegratedFormProvider>
    );
}

export type EditHospitalDrawerType = typeof EditHospitalDrawer;
